import { nudosGreen300, nudosRed100, nudosYellow200 } from '../styles/styles.constants';
import { TnudosCare } from './global';
import { IAdditionalActions, IData } from './orders';

export enum CONFIG_TYPE_ID {
  BASIC = 1,
  ADVANCE = 2,
  WIPING = 3,
  WARRANTY = 4,
  DIAGNOSTIC = 5
}

export enum CONFIG_CLASS_ID {
  DEP = 1,
  APPLE_CARE_PLUS = 2,
  MDM_ENROLLMENT = 3,
  APPLE_CONFIGURATOR = 4,
  HASH_ID = 5,
  CREAR_ADMIN = 6,
  ENCRIPTAR_BITLOCKER = 7,
  CONFIG_AVANZADA = 8,
  WIPING = 9,
  GARANTIA_1_ANO = 10,
  GARANTIA_3_ANOS = 11,
  DIAGNOSTICO = 12,
  BASIC_CONFIGURATION = 13
}

export const CONFIG_CLASS_FILTER_NAME = [
  'DEP',
  'Apple Care+',
  'MDM Enrollment',
  'Apple Configurator',
  'Hash ID',
  'Creación de Admin',
  'Bitlocker',
  'Conf. avanzada',
  'Garantía 1 año',
  'Garantía 3 años',
  'Diagnóstico',
  'Conf. básica'
];

export const CONFIG_FIELD_NAME_ID = {
  COMMENTS: 1,
  FILE_URL: 2,
  COMPANY_APPLE_ID: 3,
  EMAIL_APPLE_ACCOUNT: 4,
  COMPANY_NAME: 5,
  APPLE_CONFIGURATOR_USER: 6,
  APPLE_CONFIGURATOR_PASSWORD: 7,
  APPLE_CONFIGURATOR_PHONE: 8,
  ADMIN_NAME: 9,
  ADMIN_PASSWORD: 10,
  ASK_QUESTIONS: 11
} as const;
export type TConfigFieldNameId = typeof CONFIG_FIELD_NAME_ID[keyof typeof CONFIG_FIELD_NAME_ID];

export const CONFIG_STATUS_ID = {
  TO_COMPLETE: 1,
  COMPLETED: 2,
  NOT_COMPLETED: 3
} as const;
export type TConfigStatusId = typeof CONFIG_STATUS_ID[keyof typeof CONFIG_STATUS_ID];

export const CONFIG_SOURCE_REFERENCE_ID = {
  ORDER: 1,
  LOGISTIC_SERVICE: 2
} as const;
export type TConfigSourceReferenceId = typeof CONFIG_SOURCE_REFERENCE_ID[keyof typeof CONFIG_SOURCE_REFERENCE_ID];

export const CONFIG_ADDITIONAL_ACTION_ID = {
  CONFIRM_ENROLLMENT: 1
} as const;
export type TConfigAdditionalActionId = typeof CONFIG_ADDITIONAL_ACTION_ID[keyof typeof CONFIG_ADDITIONAL_ACTION_ID];

export interface IConfigDataItemCreate {
  fieldNameId: number;
  fieldValue: string | File;
}

export interface IOrganizationConfigCreate {
  organizationId: number;
  configClassId: number;
  name?: string;
  configData: IConfigDataItemCreate[];
  file?: File;
}

export interface IConfigDataItemUpdate extends IConfigDataItemCreate {}

export interface IOrganizationConfigUpdate {
  name?: string;
  configData: IConfigDataItemUpdate[];
}

export interface IConfigDataFieldName {
  id: number;
  name: string;
  nameStringId: string;
}

export interface IConfigDataItem {
  fieldName: IConfigDataFieldName;
  fieldValue: string;
}

export interface IOrganizationConfig {
  id: number;
  uuid: string;
  organizationId: number;
  configClassId: number;
  name?: string;
  description: string | null;
  configData?: IConfigDataItem[];
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface IProductConfigurationCreate {
  organizationId: number;
  productId: number;
  organizationConfigId: number;
  sourceReferenceId: number;
  sourceId: number;
  price?: number;
  resellerId?: number;
  reseller?: string;
}

export interface IConfigEvidenceItem {
  fieldNameId: number;
  fieldValue: string;
}

export interface IProductConfigurationUpdate {
  uuid: string;
  configStatusId: number;
  configEvidence: IConfigEvidenceItem[];
}

export interface IProductConfigurationUpdateReseller {
  uuid: string;
  resellerId: number;
  reseller: string;
}

export interface IProductConfiguration {
  id: number;
  uuid: string;
  productId: number;
  organizationConfigId: number;
  sourceReferenceId: number;
  sourceId: number;
  price: number | null;
  status: string;
  validUntil: string | null;
  resellerId: number | null;
  reseller: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface IRequiredConfigData {
  configClassId: CONFIG_CLASS_ID;
  configDataFieldNameId: TConfigFieldNameId;
}

export interface IConfigExclusion {
  configClassId: CONFIG_CLASS_ID;
  excludedConfigClassId: CONFIG_CLASS_ID;
}

export interface IRequiredConfigEvidence {
  configClassId: CONFIG_CLASS_ID;
  evidenceNameId: number;
}

export interface IConfigEvidenceFieldName {
  id: number;
  uuid: string;
  name: string;
  nameStringId: string;
}

export interface IConfigClassRequirements {
  configClassId: CONFIG_CLASS_ID;
  requiredEvidence: number[];
  excludedClasses: CONFIG_CLASS_ID[];
}

export interface IConfigValidation {
  isValid: boolean;
  missingEvidence: number[];
  conflicts: CONFIG_CLASS_ID[];
}

export interface ICompleteOrganizationConfig extends IOrganizationConfig {
  configData: IConfigDataItem[];
  evidence?: IConfigEvidenceItem[];
  validation: IConfigValidation;
}

export interface IConfigEvidenceItem {
  evidenceNameId: number;
  value: string;
}

export interface IConfigStatus {
  id: TConfigStatusId;
  uuid: string;
  name: string;
  nameStringId: string;
}

export interface IConfigSourceReference {
  id: TConfigSourceReferenceId;
  name: string;
}

export interface IProductConfigAdditionalActionConfirmDto {
  uuid: string;
}

export interface IConfigurationTool {
  id: number;
  name: string;
  sku: string;
  serial: string;
  pills: { [key: string]: string | null };
  careType: TnudosCare;
}

export interface IConfigurationAuthor {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  photoUrl?: string;
}

export interface IReviewConfigData {
  fieldValue: string;
  fieldName: {
    id: number;
    name: string;
    nameStringId: string;
  };
}

export interface IConfigurationData {
  id: number;
  uuid: string;
  organizationId: number;
  configClassId: number;
  name: string;
  author: IConfigurationAuthor;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  configData: IReviewConfigData[];
  isActive: boolean;
  tools: IConfigurationTool[];
}

export enum CONFIGURATION_STATUS {
  ACTIVE = 'Activo',
  INACTIVE = 'Inactivo'
}

export enum LS_ADDITIONAL_SERVICE_TYPE {
  WIPING = 'Wiping',
  BASIC_CONFIG = 'Configuración básica',
  ADVANCED_CONFIG = 'Configuración avanzada'
}

export enum CONFIG_STATUS_NAME {
  TO_COMPLETE = 'Por completar',
  NOT_COMPLETED = 'No completado',
  COMPLETED = 'Completado'
}

export enum LS_ADDITIONAL_SERVICE_NOT_COMPLETE_REASON {
  MDM = 'MDM',
  APPLE_ID = 'Bloqueado por Apple ID',
  USER_BLOCK = 'Bloqueado por el usuario',
  DISFUNCTION = 'Equipo no funcional',
  OTHER = 'Otras razones'
}

export enum LS_ADDITIONAL_SERVICE_NOT_COMPLETE_REASON_ID {
  MDM = 1,
  APPLE_ID,
  USER_BLOCK,
  DISFUNCTION,
  OTHER
}

export const CONFIG_STATUS_NAME_COLOR = {
  [`${CONFIG_STATUS_NAME.TO_COMPLETE}`]: nudosYellow200,
  [`${CONFIG_STATUS_NAME.NOT_COMPLETED}`]: nudosRed100,
  [`${CONFIG_STATUS_NAME.COMPLETED}`]: nudosGreen300
};

export interface IadditionalServiceStatusLogEntry {
  statusName?: string | null;
  statusNameStringId?: string | null;
  createdAt?: string | null;
  validUntil?: string | null;
}

export interface IlsAdditionalServiceStatus {
  statusId: number;
  statusName: CONFIG_STATUS_NAME | null;
  statusNameStringId: string | null;
  notCompletedReason?: string | null;
  notCompletedReasonId: number | null;
  notCompletedReasonStringId?: number | null;
  otherReasonComment: string | null;
  imageUrl?: string | null;
  configurationImageUrls?: string[] | null;
  createdAt?: string | null;
  isExternalService?: boolean | null;
  statusViewedByUserId?: number | null;
  evidenceComments?: string | null;
}

export interface IadditionalServiceDataForToolLogistics {
  id?: number | null;
  name?: string | null;
  uuid?: string | null;
  comments?: string | null;
  configData?: IData[] | null;
  description?: string | null;
  status?: IlsAdditionalServiceStatus | null;
  statusLog?: IadditionalServiceStatusLogEntry[] | null;
  configClassName?: string;
  configClassId?: number;
  additionalActions?: IAdditionalActions[];
  reseller?: string | null;
}

export interface DTOupdateAdditionalService {
  logisticAdditionalServiceProductId: number;
  configStatusId: number;
  logisticAdditionalServiceNotCompletedReasonId?: LS_ADDITIONAL_SERVICE_NOT_COMPLETE_REASON_ID;
  otherReasonComment?: string;
  imageUrl?: string;
}

export interface DTOmarkAdditionalServiceAsExternal {
  productId: number;
  logisticServiceId: number;
  configClassId: CONFIG_CLASS_ID;
}

export interface DTOunmarkExternalAdditionalService {
  logisticAdditionalServiceProductId: number;
  configClassId: CONFIG_CLASS_ID;
}

export interface DTOupdateAdditionalServiceStatus {
  logisticAdditionalServiceProductId: number;
  configStatusId: number;
  logisticAdditionalServiceNotCompletedReasonId?: LS_ADDITIONAL_SERVICE_NOT_COMPLETE_REASON_ID | null;
  otherReasonComment?: string | null;
  imageUrl?: string;
}

export interface IadditionalServiceNotCompleteReason {
  id: LS_ADDITIONAL_SERVICE_NOT_COMPLETE_REASON_ID;
  name: LS_ADDITIONAL_SERVICE_NOT_COMPLETE_REASON;
  nameStringId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export enum ADDITIONAL_TYPE {
  CONFIGURATION = 'configuration',
  SERVICE = 'service'
}

export enum ADDITIONAL_SERVICE_CLASS_TYPE {
  CREATE_ADMIN = 'Crear Admin',
  HASH_ID = 'Hash ID',
  DEP = 'DEP'
}

export enum ADDITIONAL_SERVICE_EVIDENCE_FIELD_NAME_ID {
  EVIDENCE_COMMENTS = 2
}

export enum ADDITIONAL_SERVICE_STATUS_MODAL_VALUES {
  COMPLETED = 'completed',
  NOT_COMPLETED = 'notCompleted',
  SEE_NOT_COMPLETED_REASONS = 'seeNotCompletedReasons'
}

export interface IEvidence {
  evidence?: string;
  name?: 'fileUrl' | 'comments' | 'notCompletedReason';
}

export interface IConfiguration {
  name: string;
  status: CONFIG_STATUS_NAME;
  uuid: string;
  evidence: IEvidence[];
  data: IData[];
  additionalActions?: IAdditionalActions[];
  configClassName?: string;
  configClassId?: number;
  reseller?: string | null;
  resellerId?: number | null;
}
